import { mergeAttributes, Node } from '@tiptap/core'
import Suggestion, { SuggestionOptions } from '@tiptap/suggestion'
import { PluginKey } from 'prosemirror-state'

export type TemplateVariableOptions = {
    HTMLAttributes: Record<string, any>,
    suggestion: Omit<SuggestionOptions, 'editor'>,
}

export const TemplateVariablePluginKey = new PluginKey('TemplateVariable')

export const TemplateVariable = Node.create<TemplateVariableOptions>({
    name: 'TemplateVariable',

    addOptions() {
        return {
            HTMLAttributes: {},
            suggestion: {
                char: '<$',
                pluginKey: TemplateVariablePluginKey,
                command: ({ editor, range, props }) => {
                    const nodeAfter = editor.view.state.selection.$to.nodeAfter;
                    const overrideSpace = nodeAfter?.text?.startsWith(' ');
                    if (overrideSpace) {
                        range.to += 1;
                    }
                    editor.chain().focus().insertContentAt(range, [
                        {
                            type: this.name,
                            attrs: props,
                        },
                        {
                            type: 'text',
                            text: ' ',
                        },
                    ]).run();
                    window.getSelection()?.collapseToEnd();
                },
                allow: ({ state, range }) => {
                    const $from = state.doc.resolve(range.from);
                    const type = state.schema.nodes[this.name];
                    const allow = !!$from.parent.type.contentMatch.matchType(type);
                    return allow;
                },
            },
        }
    },

    group: 'inline',
    inline: true,
    selectable: true,
    atom: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-id'),
                renderHTML: attributes => {
                    if (!attributes.id) {
                        return {};
                    }

                    return {
                        'data-id': attributes.id,
                    }
                },
            },
            name: {
                default: null,
                parseHTML: element => element.getAttribute('name'),
                renderHTML: attributes => {
                    if (!attributes.name) {
                        return {};
                    }

                    return {
                        'name': attributes.name,
                    }
                },
            },
            refId: {
                default: null,
                parseHTML: element => element.getAttribute('data-refId'),
                renderHTML: attributes => {
                    if (!attributes.refId) {
                        return {};
                    }

                    return {
                        'data-refId': attributes.refId,
                    }
                },
            },

        }
    },

    parseHTML() {
        return [
            {
                tag: `span[data-type="${this.name}"]`,
            },
        ]
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'span',
            mergeAttributes({ 'data-type': this.name }, this.options.HTMLAttributes, HTMLAttributes),
            [
                'span',
                { class: 'vz-tpl-var', title: node.attrs.name || '' },
                '<$ ' + node.attrs.id + ' $>'
            ]
        ]
    },

    addKeyboardShortcuts() {
        return {
            Backspace: () => this.editor.commands.command(({ tr, state }) => {
                let isTemplateVariable = false;
                const { selection } = state;
                const { empty, anchor } = selection;

                if (!empty) {
                    return false;
                }

                state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
                    if (node.type.name === this.name) {
                        isTemplateVariable = true;
                        tr.insertText(this.options.suggestion.char || '', pos, pos + node.nodeSize);
                        return false;
                    }
                    return;
                });
                return isTemplateVariable;
            })
        }
    },

    addProseMirrorPlugins() {
        return [
            Suggestion({
                editor: this.editor,
                ...this.options.suggestion,
            }),
        ]
    },
})
